import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag, Menu, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCart } from "@/context/CartContext";

export default function Nav() {
  const { count, setOpen } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goHash = (hash) => {
    setMobile(false);
    if (location.pathname !== "/") {
      navigate("/" + hash);
    } else {
      document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const links = [
    { label: "OneKind", hash: "#onekind" },
    { label: "One Skin", hash: "#oneskin" },
    { label: "Manifesto", hash: "#manifesto" },
  ];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      data-testid="site-nav"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-stone/70 border-b border-ink/5" : "bg-transparent"
      }`}
    >
      <nav className="flex justify-between items-center py-4 px-6 md:px-12">
        <Link to="/" data-testid="nav-logo" className="flex flex-col leading-none">
          <span className="font-serif text-2xl tracking-tight">OneKind</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-pharma">
            Healthcare
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.label}
              onClick={() => goHash(l.hash)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="font-sans text-sm tracking-wide hover:text-pharma transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-pharma group-hover:w-full transition-all duration-300" />
            </button>
          ))}

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-shop-dropdown"
              className="font-sans text-sm tracking-wide hover:text-derma transition-colors outline-none"
            >
              Shop
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="font-sans">
              <DropdownMenuItem onClick={() => navigate("/shop")} data-testid="nav-shop-all">
                All Products
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/shop?brand=onekind")} data-testid="nav-shop-onekind">
                OneKind — Pharma & Wellness
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/shop?brand=oneskin")} data-testid="nav-shop-oneskin">
                One Skin — Skincare
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            onClick={() => setOpen(true)}
            data-testid="nav-cart-button"
            className="relative p-2 hover:text-derma transition-colors"
            aria-label="Open cart"
          >
            <ShoppingBag size={20} strokeWidth={1.5} />
            {count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-0.5 -right-0.5 bg-derma text-stone text-[10px] font-mono w-4 h-4 rounded-full flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>

          <button
            onClick={() => goHash("#connect")}
            data-testid="nav-connect-cta"
            className="rounded-full px-6 py-2.5 bg-ink text-stone text-xs uppercase tracking-widest font-sans hover:bg-pharma transition-colors"
          >
            For Doctors
          </button>
        </div>

        <div className="flex md:hidden items-center gap-4">
          <button onClick={() => setOpen(true)} data-testid="nav-cart-button-mobile" className="relative p-2">
            <ShoppingBag size={20} strokeWidth={1.5} />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-derma text-stone text-[10px] font-mono w-4 h-4 rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
          <button onClick={() => setMobile((m) => !m)} data-testid="nav-mobile-toggle" className="p-2">
            {mobile ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {mobile && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          className="md:hidden bg-stone border-t border-ink/10 px-6 py-6 flex flex-col gap-5"
        >
          {links.map((l) => (
            <button key={l.label} onClick={() => goHash(l.hash)} className="text-left font-serif text-2xl">
              {l.label}
            </button>
          ))}
          <button onClick={() => { setMobile(false); navigate("/shop"); }} className="text-left font-serif text-2xl">
            Shop
          </button>
          <button onClick={() => goHash("#connect")} className="text-left font-sans text-sm uppercase tracking-widest text-pharma mt-2">
            For Doctors →
          </button>
        </motion.div>
      )}
    </motion.header>
  );
}
